import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import CustomTable from "../../../../core/custom-table";
import TrainingEnrollmentForm from "../../../../components/training-enrollment";
import CloseIcon from "@mui/icons-material/Close";
import "./index.scss";
import { ArrowBack } from "@mui/icons-material";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: "10px 20px" },
  "& .MuiDialogActions-root": { padding: theme.spacing(1) },
}));

const TrainingMonthEnrollment = ({ isModule = false, scheduleResponse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { trainings } = location.state || {};
  const [groupedTrainings, setGroupedTrainings] = useState({});
  const [showEnrollmentForm, setShowEnrollmentForm] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState({});

  useEffect(() => {
    if (trainings) {
      filterUpcomingTrainings(trainings);
    } else if (scheduleResponse.length > 0) {
      filterUpcomingTrainings(scheduleResponse);
    }
  }, [trainings, scheduleResponse]);

  // Format Date Range (Start Date - End Date)
  const formatDateRange = (startDate, endDate) => {
    const options = { day: "2-digit", month: "short", year: "numeric" };
    const start = new Date(startDate).toLocaleDateString("en-GB", options);
    const end = new Date(endDate).toLocaleDateString("en-GB", options);
    return start === end ? start : `${start} - ${end}`;
  };

  // Calculate Training Days Length
  const calculateTrainingDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
  };

  const filterUpcomingTrainings = (trainings) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let groupedData = {};

    trainings.forEach((training) => {
      const startDate = new Date(training.courseDuration.startDate);
      const trainingMonth = startDate.getMonth();
      const trainingYear = startDate.getFullYear();

      // Ensure training falls in current month, next month, or next+1 month
      const isUpcoming =
        (trainingYear === currentYear &&
          trainingMonth >= currentMonth &&
          trainingMonth <= currentMonth + 2) ||
        (trainingYear === currentYear + 1 &&
          currentMonth >= 10 &&
          trainingMonth <= currentMonth - 10);

      if (isUpcoming) {
        const key = `${trainingYear}-${String(trainingMonth + 1).padStart(
          2,
          "0"
        )}`; // Format as YYYY-MM

        if (!groupedData[key]) {
          groupedData[key] = [];
        }
        groupedData[key].push(training);
      }
    });

    // Sort grouped data keys in ascending order to ensure correct order
    const sortedGroupedData = Object.keys(groupedData)
      .sort()
      .reduce((acc, key) => {
        acc[key] = groupedData[key];
        return acc;
      }, {});

    setGroupedTrainings(sortedGroupedData);
  };

  const enrollBtnClick = (data) => {
    setSelectedTraining(data);
    setShowEnrollmentForm(true);
  };

  const handleCloseEnrollment = () => {
    setShowEnrollmentForm(false);
  };

  const tableConfig = {
    HeaderConfig: [
      {
        label: "Date",
        render: (data) =>
          formatDateRange(
            data.courseDuration.startDate,
            data.courseDuration.endDate
          ),
      },
      {
        label: "Model",
        id: "model",
      },
      {
        label: "Number of Days",
        render: (data) =>
          `${calculateTrainingDays(
            data.courseDuration.startDate,
            data.courseDuration.endDate
          )} days`,
      },
      {
        label: "Approval Status",
        render: (data) => (
          <span className={`status-${data?.status?.toLowerCase()}`}>
            {data?.status}
          </span>
        ),
      },
      {
        label: "Enroll",
        render: (data) =>
          ["completed", "booked"].includes(data?.status.toLowerCase()) ? (
            <Button variant="contained" color="primary" size="small" disabled>
              Enroll
            </Button>
          ) : ["rejected"].includes(data?.status.toLowerCase()) ? (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => enrollBtnClick(data)}
            >
              Re Enroll
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => enrollBtnClick(data)}
            >
              Enroll
            </Button>
          ),
      },
    ],
  };

  const handleBackNavigation = () => {
    navigate("/oem/training/training-year-view");
  };

  return (
    <>
      {!isModule && (
        <div className="training-title-container">
          <ArrowBack fontSize="small" onClick={() => handleBackNavigation()} />
          <div>
            <span className="training-title">Training</span>
            <div className="training-schedule">Three months schedule</div>
          </div>
        </div>
      )}

      {Object.keys(groupedTrainings).length ? (
        <div
          className="month-view-container2"
          style={{ margin: isModule ? "0px" : "15px 30px 20px 32px" }}
        >
          {isModule && (
            <Typography className="primary-text" variant="h6" fontWeight="bold">
              Three months schedule
            </Typography>
          )}
          {Object.keys(groupedTrainings).map((monthKey) => {
            const [year, month] = monthKey.split("-");
            const monthName = new Date(year, month - 1).toLocaleString(
              "en-GB",
              {
                month: "long",
                year: "numeric",
              }
            );

            return (
              <div key={monthKey} style={{ marginBottom: "30px" }}>
                <h2>{monthName}</h2>
                <CustomTable
                  header={tableConfig.HeaderConfig}
                  data={groupedTrainings[monthKey]}
                />
              </div>
            );
          })}

          {showEnrollmentForm && (
            <BootstrapDialog
              onClose={handleCloseEnrollment}
              aria-labelledby="customized-dialog-title"
              open={showEnrollmentForm}
              fullWidth
              maxWidth="lg"
            >
              <DialogTitle sx={{ m: 0, p: 2, fontWeight: "600" }}>
                TRAINING ENROLLMENT
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleCloseEnrollment}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
              <DialogContent dividers sx={{ border: 0 }}>
                <TrainingEnrollmentForm
                  startDate={selectedTraining?.courseDuration?.startDate}
                  endDate={selectedTraining?.courseDuration?.endDate}
                  courseId={selectedTraining?.id}
                />
              </DialogContent>
            </BootstrapDialog>
          )}
        </div>
      ) : (
        <Typography
          sx={{ textAlign: "center", mt: 3, color: "gray" }}
          variant="h4"
        >
          {!isModule && "No Records"}
        </Typography>
      )}
    </>
  );
};

export default TrainingMonthEnrollment;
